import { DayHours, Break } from '../types';

/**
 * Data transformation utilities for Business Hours
 *
 * Database format (snake_case):
 * {
 *   is_active: boolean,
 *   start_time: string,
 *   end_time: string,
 *   breaks: Array<{id: string, start_time: string, end_time: string}>
 * }
 *
 * UI format (camelCase):
 * {
 *   isActive: boolean,
 *   startTime: string,
 *   endTime: string,
 *   breaks: Array<{id: string, start_time: string, end_time: string}>
 * }
 *
 * Note: Breaks maintain snake_case as they're directly from DB
 */

// Convert from snake_case (DB) to camelCase (UI)
// Handles null/undefined gracefully with default values
export function convertToUI(dbHours: any): {
  isActive: boolean;
  startTime: string;
  endTime: string;
  breaks: Break[];
} {
  return {
    isActive: dbHours?.is_active ?? false,
    startTime: dbHours?.start_time ?? '09:00',
    endTime: dbHours?.end_time ?? '17:00',
    breaks: (dbHours?.breaks || []).map((b: any) => ({
      id: b.id,
      start_time: b.start_time,
      end_time: b.end_time
    }))
  };
}

// Convert from camelCase (UI) to snake_case (DB)
// Handles null/undefined gracefully
export function convertToDB(uiHours: {
  isActive: boolean;
  startTime: string;
  endTime: string;
  breaks?: Break[];
}): DayHours {
  return {
    is_active: uiHours.isActive,
    start_time: uiHours.startTime,
    end_time: uiHours.endTime,
    breaks: uiHours.breaks || []
  };
}

// Validate business hours structure and values
// Returns null if valid, error message string if invalid
export function validateBusinessHours(hours: any): string | null {
  if (!hours) return 'חסרים נתוני שעות פעילות';

  if (!hours.regular_hours) return 'חסרות שעות פעילות קבועות';

  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  for (const day of days) {
    const dayHours = hours.regular_hours[day];
    if (!dayHours) continue;

    if (dayHours.is_active) {
      if (!dayHours.start_time || !dayHours.end_time) {
        return `חסרות שעות פעילות ליום ${getDayName(day)}`;
      }

      // בדוק שהשעה ההתחלה לפני השעה הסיום
      if (dayHours.start_time >= dayHours.end_time) {
        return `שעת הסיום חייבת להיות אחרי שעת ההתחלה ב${getDayName(day)}`;
      }

      // בדוק שההפסקות בטווח השעות
      if (dayHours.breaks && dayHours.breaks.length > 0) {
        for (const brk of dayHours.breaks) {
          if (brk.start_time < dayHours.start_time || brk.end_time > dayHours.end_time) {
            return `הפסקה ב${getDayName(day)} חורגת משעות הפעילות`;
          }
          if (brk.start_time >= brk.end_time) {
            return `שעת סיום ההפסקה חייבת להיות אחרי שעת ההתחלה ב${getDayName(day)}`;
          }
        }
      }
    }
  }

  return null;
}

function getDayName(dayKey: string): string {
  const names: { [key: string]: string } = {
    sunday: 'ראשון',
    monday: 'שני',
    tuesday: 'שלישי',
    wednesday: 'רביעי',
    thursday: 'חמישי',
    friday: 'שישי',
    saturday: 'שבת'
  };
  return names[dayKey] || dayKey;
}
